import { Link } from '@/i18n/routing';
import type { BlogPost } from '@/types/blog';
import type * as React from 'react';
import { PublicPostCard } from './PublicPostCard';

/**
 * Inline "Blog" section embedded on the existing public lawyer profile
 * (`/advokati/[id]`). Lazy-renders: returns `null` when the author has no
 * published posts so the section never appears for non-bloggers.
 *
 * Server component — no `'use client'`. Receives posts pre-fetched (max 3)
 * by the parent so the lawyer profile page can decide whether to inject
 * this tab without an extra waterfall.
 */

interface Props {
  professionalId: string;
  locale: string;
  posts: BlogPost[];
}

const PREVIEW_LIMIT = 3;

export function BlogProfileTab({
  professionalId,
  locale,
  posts,
}: Props): React.ReactElement | null {
  // Lazy: only render when the author actually has published posts. Without
  // this guard the section header would show even for lawyers who have never
  // written — feels like an empty stub.
  if (!posts || posts.length === 0) return null;

  const visible = posts.slice(0, PREVIEW_LIMIT);
  // Same hand-built path pattern as `PublicPostCard` — the
  // `/advokati/[id]/blog(/[slug])` route is added to `i18n/routing.ts` in the
  // same phase so future call-sites can use the typed `Link` shape.
  const blogIndexPath = {
    pathname: '/advokati/[id]/blog' as const,
    params: { id: professionalId },
  };

  return (
    <section
      aria-label={locale === 'en' ? 'Blog posts' : 'Blogové príspevky'}
      className='mt-8'
    >
      <header className='mb-4 flex items-center justify-between'>
        <h2 className='text-xl font-semibold text-content-primary'>
          {locale === 'en' ? 'Blog' : 'Blog'}
        </h2>
        <Link
          href={blogIndexPath}
          className='inline-flex items-center text-sm font-medium text-brand-tertiary hover:text-brand-tertiary-hover'
        >
          {locale === 'en' ? 'View all' : 'Zobraziť všetky'}
          <span aria-hidden='true' className='ml-1'>
            →
          </span>
        </Link>
      </header>
      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {visible.map((post) => (
          <PublicPostCard
            key={post.id}
            post={post}
            professionalId={professionalId}
            locale={locale}
          />
        ))}
      </div>
    </section>
  );
}
